'use client';

/**
 * useAuthGate — state machine for the full-screen captcha gate.
 *
 * Drives AuthGateModal: owns the open/closed state, the gate session
 * lifecycle (start → submit → continue/passed/failed), and the
 * failure counters that decide when the gate is required.
 *
 * Pass `identifier` (email/phone being typed) to get per-identifier
 * failure tracking; omit it on pages without an identifier field.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AUTH_GATE_FAILURE_THRESHOLD,
  clearAllIdentifierFailures,
  clearCaptchaPassToken,
  clearGateFlag,
  clearIdentifierFailures,
  getCaptchaPassToken,
  getIdentifierFailureCount,
  hasFlaggedIdentifier,
  isAuthGateRequired,
  isGateFlagged,
  markGateRequired,
  recordIdentifierFailure,
  startGateSession,
  storeCaptchaPassToken,
  submitGateAnswer,
} from '@/lib/authGate';
import type { GateStatus, GateStep } from '@/lib/authGate';

export type { GateStatus };

const PASSED_CLOSE_DELAY_MS = 1200;

export function useAuthGate(identifier?: string) {
  const [open, setOpen] = useState<boolean>(() => isAuthGateRequired());
  const [status, setStatus] = useState<GateStatus>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [step, setStep] = useState<GateStep | null>(null);
  const [stepNumber, setStepNumber] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState(0);
  const startingRef = useRef(false);
  const submittingRef = useRef(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Identifier switch: if the newly-typed identifier is already flagged
  // locally, the gate opens (same trigger the inline captcha used).
  useEffect(() => {
    if (!identifier || open) return;
    if (getIdentifierFailureCount(identifier) >= AUTH_GATE_FAILURE_THRESHOLD && !getCaptchaPassToken()) {
      setOpen(true);
    }
  }, [identifier, open]);

  const startSession = useCallback(async () => {
    if (startingRef.current) return;
    startingRef.current = true;
    setStatus('loading');
    setError(null);
    try {
      const { sessionId: sid, step: firstStep } = await startGateSession();
      setSessionId(sid);
      setStep(firstStep);
      setStepNumber(1);
      setStatus('step');
    } catch (err) {
      const blocked = (err as { blocked?: boolean }).blocked === true;
      if (blocked) {
        setRetryAfter((err as { retryAfter?: number }).retryAfter || 600);
        setStatus('blocked');
      } else {
        setError((err as Error).message || 'Failed to load security check');
        setStatus('failed');
      }
    } finally {
      startingRef.current = false;
    }
  }, []);

  // Fetch the first step whenever the gate opens without a live session
  useEffect(() => {
    if (open && !sessionId && status === 'idle' && !startingRef.current) {
      void startSession();
    }
  }, [open, sessionId, status, startSession]);

  const finish = useCallback((passToken: string) => {
    storeCaptchaPassToken(passToken);
    clearGateFlag();
    clearAllIdentifierFailures();
    setStatus('passed');
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      setStatus('idle');
      setSessionId(null);
      setStep(null);
      setStepNumber(0);
      setError(null);
    }, PASSED_CLOSE_DELAY_MS);
  }, []);

  const submitAnswer = useCallback(
    async (answer: string | number[]) => {
      if (!sessionId || !step || status === 'submitting' || submittingRef.current) return;
      submittingRef.current = true;
      setStatus('submitting');
      setError(null);
      try {
        const result = await submitGateAnswer(sessionId, step.id, answer);

        if (result.status === 'continue' && result.step) {
          setStep(result.step);
          setStepNumber(n => n + 1);
          setStatus('step');
        } else if (result.status === 'passed' && result.passToken) {
          finish(result.passToken);
        } else if (result.status === 'failed') {
          if (result.blocked) {
            setRetryAfter(result.retryAfter || 600);
            setStatus('blocked');
          } else {
            setStatus('failed');
          }
        } else {
          // invalid/expired session — silently start a fresh one
          setSessionId(null);
          setStep(null);
          setStatus('idle');
        }
      } catch (err) {
        setError((err as Error).message || 'Verification failed — please try again');
        setStatus('failed');
      } finally {
        submittingRef.current = false;
      }
    },
    [sessionId, step, status, finish],
  );

  /** Start a fresh session (failed / blocked screens). */
  const restart = useCallback(() => {
    setSessionId(null);
    setStep(null);
    setStepNumber(0);
    setError(null);
    setStatus('idle');
  }, []);

  /** Force the gate open — called when an auth response says requires_captcha. */
  const require = useCallback(() => {
    clearCaptchaPassToken(); // any stored pass token is stale by definition
    markGateRequired();
    setOpen(true);
  }, []);

  /** Record a failed auth attempt for the current identifier. */
  const recordFailure = useCallback(() => {
    if (!identifier) return;
    const count = recordIdentifierFailure(identifier);
    if (count >= AUTH_GATE_FAILURE_THRESHOLD) {
      markGateRequired();
      setOpen(true);
    }
  }, [identifier]);

  /** Clear the current identifier's local failure count (login success). */
  const clearFailures = useCallback(() => {
    if (identifier) {
      clearIdentifierFailures(identifier);
    }
    if (!isGateFlagged() && !hasFlaggedIdentifier()) {
      setOpen(false);
    }
  }, [identifier]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  return {
    open,
    status,
    step,
    stepNumber,
    error,
    retryAfter,
    submitAnswer,
    restart,
    require,
    recordFailure,
    clearFailures,
  };
}

export type UseAuthGate = ReturnType<typeof useAuthGate>;
